import makeStyles from '@mui/styles/makeStyles';
import NROrgSelectorStyles from './NROrgSelector.styles';
import Divider from '@mui/material/Divider';
import MenuItem from '@mui/material/MenuItem';
import Check from '@mui/icons-material/Check';
import Button from '@mui/material/Button';
import Menu from '@mui/material/Menu';
import { useContext, useEffect, useState } from 'react';
import KeyboardArrowDown from '@mui/icons-material/KeyboardArrowDown';
import CardHeader from '@mui/material/CardHeader';
import { useAuth0 } from '../../../auth/useAuth0';
import { LAST_ORG_USED_KEY } from '../../../utils/ApiConstants';
import { UserContext, ORG_REDIRECT_ATTEMPTED_KEY } from '../../../App';
import { USER_ORGS } from '../../../graphql/queries/user';
import useNRLazyQuery from '../../../graphql/useNRLazyQuery';

const useStyles = makeStyles(NROrgSelectorStyles);

function NROrgSelector() {
  const { currentOrg, currentOrgId, roles, email, setIsChangingOrg, idToken } = useContext(UserContext);
  const auth0Namespace = process.env.REACT_APP_AUTH0_NAMESPACE;
  const permissionsNamespace = auth0Namespace + 'permissions';
  const classes = useStyles();
  const { loginWithRedirect } = useAuth0();
  const [disableDropdown, setDisableDropdown] = useState(true);
  const [orgs, setOrgs] = useState(null);

  const [, , , getUserOrgs] = useNRLazyQuery({
    query: USER_ORGS,
    qlObjKey: 'userOrgs',
    options: { context: { version: 'v3' } },
    transformData: userOrgs => {
      const sortedOrgs = [...userOrgs].sort((a, b) => (a?.DisplayName || '').localeCompare(b?.DisplayName || '', undefined, { sensitivity: 'base' }));
      setOrgs(sortedOrgs);
      setDisableDropdown(sortedOrgs.length < 2);
    }
  });

  useEffect(() => {
    if (idToken && idToken[permissionsNamespace]?.length) {
      getUserOrgs();
    }
  }, [idToken]);

  async function handleOnChange(value) {
    if (!value) return;
    setIsChangingOrg(true);
    // Persist the selected org so CheckAuthentication picks it up after redirect
    localStorage.setItem(email + LAST_ORG_USED_KEY, value);
    // Pre-set the redirect guard so CheckAuthentication doesn't re-redirect
    // if the token comes back without org_id for some reason
    sessionStorage.setItem(ORG_REDIRECT_ATTEMPTED_KEY, value);
    // Full-page redirect through Auth0 with the new organization.
    // This is required because refresh token grants cannot switch orgs,
    // and iframe-based silent auth is blocked by SAML IdPs with strict framing rules.
    await loginWithRedirect({ organization: value });
  }

  /**
   * Menu handling
   */
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const handleClick = event => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <div className={classes.mainContainer}>
      {!!orgs?.length && (
        <>
          <Button
            id="org-selector-button"
            aria-controls={open ? 'org-selector-menu' : undefined}
            aria-haspopup="true"
            aria-expanded={open ? 'true' : undefined}
            variant="text"
            disableElevation
            color="inherit"
            onClick={!disableDropdown ? handleClick : undefined}
            endIcon={!disableDropdown && <KeyboardArrowDown />}
            sx={{ maxHeight: '36px', ...(!disableDropdown && { paddingRight: '12px' }) }}
            classes={{ root: classes.button, disabled: classes.buttonDisabled, endIcon: classes.endIcon }}
            disabled={disableDropdown}
          >
            {currentOrg}
          </Button>
          <Menu
            id="org-selector-menu"
            aria-labelledby="org-selector-button"
            anchorEl={anchorEl}
            open={open}
            onClose={handleClose}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'left'
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'left'
            }}
            classes={{ paper: classes.menuPaper }}
          >
            <CardHeader
              titleTypographyProps={{ className: classes.titleStyle }}
              subheaderTypographyProps={{ className: classes.subHeaderStyle }}
              title={email}
              subheader={!!roles && roles?.[0]}
              className={classes.menuHeader}
            />
            <Divider className={classes.menuDivider} />
            {!!orgs &&
              orgs.map(({ Id, DisplayName }) => (
                <MenuItem
                  selected={currentOrgId === Id}
                  classes={{ root: classes.menuItemRoot }}
                  onClick={() => currentOrgId !== Id && handleOnChange(Id)}
                  key={Id}
                >
                  {DisplayName}
                  {currentOrgId === Id && <Check className={classes.selectedIcon} />}
                </MenuItem>
              ))}
          </Menu>
        </>
      )}
    </div>
  );
}
export default NROrgSelector;
